import { useCallback, useMemo } from 'react'
import type { AudioNoteState } from '@shared/types/AudioNoteState'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { useGlobalState } from '@highlight/ipc/react'
import { DetectedMeeting, GoogleMeetMeeting, RecallDetectedMeeting } from '@highlight/common'
import { useAudioFeaturesPermission } from '../settings/useAudioFeaturesPermission'
import { GlobalState } from '@highlight/ipc'

/**
 * Write-only toggle for starting/stopping audio notes. Reads the current
 * status imperatively, so callers don't subscribe to the (frequently
 * broadcast) audio-note-state object just to flip recording. Resolves true
 * when a session is running or on its way (see
 * `AudioNoteStateManager.startRecording`).
 */
export const useToggleTakingNotes = () => {
  const { setAreAudioFeaturesEnabled } = useAudioFeaturesPermission()

  return useCallback(
    async (meeting?: DetectedMeeting | GoogleMeetMeeting | RecallDetectedMeeting): Promise<boolean> => {
      setAreAudioFeaturesEnabled(true)
      const audioNoteStatus = GlobalState.get<AudioNoteState>('audio-note-state')?.status
      if (audioNoteStatus === 'idle' || audioNoteStatus === 'saved') {
        return AudioNoteIpcRenderer.startRecording(meeting)
      }
      await AudioNoteIpcRenderer.stopRecording(false)
      return false
    },
    [setAreAudioFeaturesEnabled],
  )
}

/**
 * Hook to access and manage audio note state
 */
export const useAudioNoteState = () => {
  const [state] = useGlobalState<AudioNoteState | undefined>('audio-note-state', undefined)
  const toggleTakingNotes = useToggleTakingNotes()

  const startRecording = useCallback(async (meeting?: DetectedMeeting | GoogleMeetMeeting | RecallDetectedMeeting) => {
    await AudioNoteIpcRenderer.startRecording(meeting)
  }, [])

  const stopRecording = useCallback(async (save = false) => {
    await AudioNoteIpcRenderer.stopRecording(save)
  }, [])

  return useMemo(() => {
    const savedNote = state?.savedNote
    const isAudioNoteActive = state?.status !== 'idle' || !!savedNote

    const displayTitle = state?.savedNote?.title || state?.activeNoteTitle || 'Meeting Note'

    return {
      state,
      startRecording,
      stopRecording,
      toggleTakingNotes,
      // Derived state for convenience
      isTakingNotes: state?.status === 'recording' || state?.status === 'starting' || state?.status === 'reconnecting',
      isRecording: state?.status === 'recording' || state?.status === 'paused' || state?.status === 'reconnecting',
      isStarting: state?.status === 'starting',
      isPaused: state?.status === 'paused',
      isSaving: state?.status === 'saving',
      isReconnecting: state?.status === 'reconnecting',
      savedNote,
      displayTitle,
      isAudioNoteActive,
      // Summary state helpers
      isSummaryPending: state?.summaryPending ?? false,
      summaryError: state?.summaryError ?? null,
      // Active note state
      startTime: state?.startTime ?? null,
      // Awaiting meeting join state
      awaitingMeetingJoin: state?.awaitingMeetingJoin ?? null,
    }
  }, [state, startRecording, stopRecording, toggleTakingNotes])
}
